import type { CollectionBindingOverride } from '@vibesites/runtime';

// Copied from the parent theme for the local PropertyOverview override.
// Keep in sync if the base utility changes.

/** Minimal amenity shape needed for featured / desirability sorting. */
export interface SortableAmenity {
  title?: string | null;
  is_featured?: string | null;
}

/** Truthy string values used by CMS for amenity is_featured. */
function isFeaturedAmenity(item: SortableAmenity): boolean {
  const value = String(item.is_featured ?? '').trim().toLowerCase();
  return value === 'true' || value === '1' || value === 'yes' || value === 'y';
}

/**
 * Keyword patterns ranked by desirability (lower = more desirable).
 * Matched against amenity titles case-insensitively.
 */
const DESIRABILITY_PATTERNS: Array<{ rank: number; pattern: RegExp }> = [
  // High-demand community & apartment highlights
  { rank: 10, pattern: /\b(?:pool|swimming)\b/i },
  { rank: 20, pattern: /walk[\s-]?in\s+closet/i },
  { rank: 30, pattern: /\b(?:fitness|gym)\b|workout\s+center|exercise\s+room/i },
  { rank: 40, pattern: /\bdishwasher\b/i },
  // Prefer in-unit laundry over shared laundry facilities
  { rank: 50, pattern: /in[\s-]?unit.*(washer|dryer|laundry)|(washer|dryer|laundry).*in[\s-]?unit/i },
  { rank: 55, pattern: /\bwasher\b.*\bdryer\b|\bdryer\b.*\bwasher\b|washer\s*\/\s*dryer/i },
  { rank: 60, pattern: /\b(?:patio|balcony|terrace|deck)\b/i },
  // Strong secondary draws
  { rank: 70, pattern: /\bspa\b|hot\s+tub|sauna|steam\s+room/i },
  { rank: 80, pattern: /hardwood|wood\s+floor|vinyl\s+plank|\blvp\b/i },
  { rank: 90, pattern: /stainless|granite|quartz|stone\s+counter/i },
  { rank: 100, pattern: /air\s+condition|central\s+air|\bac\b|climate\s+control/i },
  { rank: 110, pattern: /clubhouse|resident\s+lounge|sky\s+lounge|rooftop/i },
  { rank: 120, pattern: /dog\s+park|pet\s+park|pet\s+spa|pet\s+wash/i },
  { rank: 130, pattern: /package\s+(?:lockers?|room|center)|concierge/i },
  { rank: 140, pattern: /\bgarage\b|covered\s+parking|assigned\s+parking/i },
  { rank: 150, pattern: /fireplace|smart\s+home|usb\s+outlet|ceiling\s+fan/i },
  // Shared laundry ranks below in-unit
  { rank: 200, pattern: /\b(?:laundry|washer|dryer)\b/i },
];

const UNRANKED = 1000;

function desirabilityRank(item: SortableAmenity): number {
  const title = item.title ?? '';
  if (!title) return UNRANKED;
  let best = UNRANKED;
  for (const { rank, pattern } of DESIRABILITY_PATTERNS) {
    if (rank < best && pattern.test(title)) best = rank;
  }
  return best;
}

/**
 * True when the collection binding editor saved a custom item order.
 * Editor reorder should win over featured / desirability sorting.
 */
export function hasCustomAmenityOrder(
  override: CollectionBindingOverride | null | undefined,
): boolean {
  if (!override) return false;
  return override.orderedIds != null || (override.ordering?.length ?? 0) > 0;
}

export interface SortAmenitiesOptions {
  /** When true, keep the binding/settings order and skip featured/desirability sort. */
  customOrder?: boolean;
}

/** Featured first, then desirable keywords, preserving relative order otherwise. */
export function sortAmenities<T extends SortableAmenity>(
  items: T[],
  options?: SortAmenitiesOptions,
): T[] {
  if (options?.customOrder) return items;
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const featuredDiff = Number(isFeaturedAmenity(b.item)) - Number(isFeaturedAmenity(a.item));
      if (featuredDiff !== 0) return featuredDiff;
      const rankDiff = desirabilityRank(a.item) - desirabilityRank(b.item);
      if (rankDiff !== 0) return rankDiff;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

/** Title-case amenity labels for display (e.g. "swimming pool" → "Swimming Pool"). */
export function toTitleCaseAmenity(value: string): string {
  return value
    .toLowerCase()
    .replace(/(^|[\s/&-])([a-z])/g, (_, boundary: string, char: string) => boundary + char.toUpperCase());
}
